import React from 'react';
import { Flag, Check } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { cn } from '../../lib/utils';
import { isSmartLightEmployee } from '../../authUtils';
import { formatSlashDate } from '../../utils/dateUtils';

// 5-step timeline. Keys mirror the WorkflowStep enum in
// backend/referral_activity/domain/workflow_rules.py; labels match
// _WORKFLOW_STEP_LABELS.
const WORKFLOW_STEPS = [
  { key: 'ACKNOWLEDGE', label: 'Acknowledged' },
  { key: 'PROVIDE_INITIAL', label: 'Provide Initial Decision' },
  { key: 'OPEN_QUESTIONS', label: 'Open Questions' },
  { key: 'UNRESOLVED', label: 'Unresolved' },
  { key: 'ONGOING_MONITOR', label: 'Ongoing/Monitor' },
];

function getStepBadgeClass(step) {
  if (step.key === 'ACKNOWLEDGE') {
    // Amber regardless of status — matches the legacy "Acknowledged" visual.
    return 'bg-status-acknowledge-bg text-amber-800 border-amber-300 font-medium';
  }
  if (step.status === 'in-progress') {
    return 'bg-status-initial/15 text-sky-700 border-status-initial/30 font-medium';
  }
  if (step.status === 'completed') {
    return 'bg-muted/50 text-foreground border-border font-medium';
  }
  return 'bg-muted text-muted-foreground border-border';
}

// liveAssignedTo / liveDueDate: post-submit overrides from the activity feed.
// null = no live update yet; fall back to the list snapshot values.
function WorkflowTimeline({ referral, currentWorkflowStep, visitedWorkflowSteps, liveAssignedTo = null, liveDueDate = null }) {
  // formatSlashDate parses YYYY-MM-DD as a date-only string (regex, no Date
  // object), so the displayed date matches the referrals table. The previous
  // `new Date(s)` formatter parsed the bare date as UTC midnight and read
  // back in local TZ, shifting the displayed day by one west of UTC.
  const publishedDate = formatSlashDate(referral?.publish_dt) || null;
  // Live values (from the activity feed) override the stale list snapshot so
  // the timeline reflects post-submit changes without a full list refetch.
  // Note: formatSlashDate formats any string with a leading 'YYYY-MM-DD'
  // prefix (full ISO datetimes included) and returns only genuinely
  // non-matching strings unchanged. Both sources here are date-only.
  const dueDate = liveDueDate !== null
    ? (formatSlashDate(liveDueDate) || null)
    : (formatSlashDate(referral?.due_date) || null);
  // assigned_to (the specific assigned user/org) is preferred over tpa_carrier
  // (the submitting carrier org) when both are present on the snapshot.
  const assignedTo = liveAssignedTo !== null
    ? liveAssignedTo
    : (referral?.assigned_to || referral?.tpa_carrier || null);

  const visited = new Set(visitedWorkflowSteps ?? []);
  const currentIdx = WORKFLOW_STEPS.findIndex((s) => s.key === currentWorkflowStep);

  // "Published" sits before ACKNOWLEDGE — it's a legacy lifecycle marker,
  // not a state machine step.
  const steps = [];
  if (publishedDate) {
    steps.push({
      key: 'PUBLISHED',
      label: 'Published',
      status: 'completed',
      date: publishedDate,
    });
  }

  // Steps up to and including the current step. Skip earlier steps the
  // referral never traversed (so a path like ACK -> ONGOING_MONITOR doesn't
  // pretend PROVIDE_INITIAL / OPEN_QUESTIONS happened).
  for (let idx = 0; idx <= currentIdx; idx += 1) {
    const s = WORKFLOW_STEPS[idx];
    if (idx === currentIdx) {
      const subParts = [assignedTo, dueDate ? `Due ${dueDate}` : null].filter(Boolean);
      steps.push({
        ...s,
        status: 'in-progress',
        subLabel: subParts.join(' · ') || null,
      });
    } else if (visited.has(s.key)) {
      steps.push({
        ...s,
        status: 'completed',
        date: null,
      });
    }
  }

  if (steps.length === 0) return null;

  return (
    <div className="drawer-timeline-container">
      <div className="drawer-timeline-box">
        {steps.map((step, idx) => (
          <React.Fragment key={step.key}>
            {step.status === 'completed' && (
              <Check className="workflow-check-icon" strokeWidth={2.5} />
            )}
            <div className={cn('workflow-step', step.status === 'completed' ? 'ml-1' : '')}>
              <Badge
                variant="outline"
                className={cn('workflow-badge', getStepBadgeClass(step))}
              >
                {step.label}
              </Badge>
              {(step.date || step.subLabel) && (
                <span className={cn('workflow-date', step.status === 'in-progress' ? 'text-foreground' : 'text-muted-foreground')}>
                  {step.date || step.subLabel}
                </span>
              )}
            </div>
            {idx < steps.length - 1 && (
              <div className={cn('workflow-connector', step.status === 'completed' ? 'border-solid border-foreground/40' : 'border-dotted border-foreground/30')} />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function DrawerHeader({
  referral,
  currentWorkflowStep = 'ACKNOWLEDGE',
  visitedWorkflowSteps = ['ACKNOWLEDGE'],
  // Live values from the drawer's activity feed. Override the values baked
  // into the referral prop (which come from the list page and don't
  // auto-update after a comment-modal submit). null = "no live update yet,
  // fall back to whatever the list already has".
  liveStatus = null,
  liveReason = null,
  liveAssignedTo = null,
  liveDueDate = null,
}) {
  const showInternalFields = isSmartLightEmployee();
  const impactTier = referral?.impact_tier || 0;
  const displayStatus = liveStatus ?? referral?.tpa_carrier_status;
  const displayReason = liveReason ?? referral?.tpa_carrier_reason;

  return (
    <div className="drawer-header-container">
      <div className="drawer-header-main">

        <div className="drawer-header-id-box">
          <p className="drawer-header-id-label">Referral ID</p>
          <p data-testid="drawer-referral-id" className="drawer-header-id-value">{referral?.referral_id || '—'}</p>
        </div>

        <div className="drawer-header-content">

          {/* Row 1 */}
          <div className="drawer-header-grid">
            <div className="drawer-header-section drawer-header-col-client">
              <p className="drawer-header-label">Client</p>
              <p className="drawer-header-value">{referral?.client || '—'}</p>
            </div>
            <div className="drawer-header-section drawer-header-col-carrier">
              <p className="drawer-header-label">TPA/Carrier</p>
              {/* tpa_carrier is the static carrier org from the referral record.
                  The timeline sublabel tracks live assigned_to separately. */}
              <p className="drawer-header-value">{referral?.tpa_carrier || '—'}</p>
            </div>
            <div className="drawer-header-section drawer-header-col-type">
              <p className="drawer-header-label">Referral Type</p>
              {referral?.referral_type ? (
                <Badge variant="outline" className="drawer-header-badge">{referral.referral_type}</Badge>
              ) : (
                <p className="drawer-header-value text-muted-foreground">—</p>
              )}
            </div>
            <div className="drawer-header-section drawer-header-col-name">
              <p className="drawer-header-label">Referral Name</p>
              <span data-testid="drawer-referral-name" className={cn('drawer-header-value truncate block pr-2 md:pr-0')} title={referral?.referral_name || ''}>
                {referral?.referral_name || '—'}
              </span>
            </div>
            <div className="drawer-header-section drawer-header-col-impact">
              <p className="drawer-header-label">Impact</p>
              <div className="flex items-center gap-0.5 pt-0.5">
                {[...Array(5)].map((_, i) => (
                  <span key={i} className={cn('impact-dot', i < impactTier ? 'bg-primary' : 'bg-muted')} />
                ))}
              </div>
            </div>
          </div>

          {/* Row 2 */}
          <div className="drawer-header-row">
            <div className="drawer-header-status-box">
              <div className="drawer-header-status-col-left">
                <p className="drawer-header-label">Status</p>
                <div className="drawer-header-status-text">
                  {displayStatus && <span>{displayStatus}</span>}
                </div>
              </div>
              <div className="drawer-header-status-col-right">
                <p className="drawer-header-label">Reason</p>
                <div className="drawer-header-status-text">
                  {displayReason && <span className="truncate block">{displayReason}</span>}
                </div>
              </div>
            </div>

            {showInternalFields && (
              <div className="drawer-header-age-section">
                <div className="drawer-header-section">
                  <p className="drawer-header-label">Referral Age</p>
                  <span className="drawer-header-value">
                    {referral?.referral_age !== null && referral?.referral_age !== undefined
                      ? `${referral.referral_age} days`
                      : '—'}
                  </span>
                </div>
                {referral?.aging_out && (
                  <div className="aging-out-badge">
                    <Flag className="h-3.5 w-3.5" />
                    <span>Aging Out</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Row 3 */}
          <WorkflowTimeline
            referral={referral}
            currentWorkflowStep={currentWorkflowStep}
            visitedWorkflowSteps={visitedWorkflowSteps}
            liveAssignedTo={liveAssignedTo}
            liveDueDate={liveDueDate}
          />

        </div>
      </div>
    </div>
  );
}

export { DrawerHeader };
